import React from "react";
import { useIntl } from "react-intl";
import { messages } from "../Recordings.messages";
import styles from "./Slides.module.scss";
import { SimpleLayoutView } from "@src/lms/jelly/extensions/layouts/simple/SimpleLayoutView";
import { nl2p } from "@src/utils/nl2p";
import { useBlocks, useUi } from "@jelly/lesson";
import * as appConfig from "appConfig";
import { SpatialCarousel } from "@src/lms/jelly/extensions/layouts";

import {
  NewSessionModal,
  MasterBusModal,
  RecordingTracksModal,
  // PremasterModal,
  // StereoFoldownAndBinauralModal,
  // StereoFoldownModal
} from "../Modals/Modals";

interface SlideProps {}

export const Slide1: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P3_S1_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P3_S1_Description))}

            <h4>{formatMessage(messages.G2_P3_S1_P1_Title)}</h4>
            {nl2p(formatMessage(messages.G2_P3_S1_P1_Description))}

            <h4>{formatMessage(messages.G2_P3_S1_P2_Title)}</h4>
            {nl2p(formatMessage(messages.G2_P3_S1_P2_Description))}

            <h4>{formatMessage(messages.G2_P3_S1_P3_Title)}</h4>
            {nl2p(formatMessage(messages.G2_P3_S1_P3_Description))}
            {nl2p(formatMessage(messages.G2_P3_S1_P4_Description))}
          </div>
        </div>
      </div>
    </SimpleLayoutView>
  );
};

export const Slide2: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const { Button } = useUi();

  // Modal state management
  const [newSessionModalOpen, setNewSessionModalOpen] = React.useState(false);
  const [masterBusModalOpen, setMasterBusModalOpen] = React.useState(false);
  const [recordingTracksModalOpen, setRecordingTracksModalOpen] = React.useState(false);

  const handleCloseNewSessionModal = React.useCallback(() => setNewSessionModalOpen(false), []);
  const handleCloseMasterBusModal = React.useCallback(() => setMasterBusModalOpen(false), []);
  const handleCloseRecordingTracksModal = React.useCallback(() => setRecordingTracksModalOpen(false), []);

  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          {/*// @ts-expect-error We cannot override layouts using Contexts :/ */}
          <SpatialCarousel showBreadcrumbs>
            {[
              // First carousel item - New Session & Master Bus
              <div key={1} className={styles.carouselItem}>
                <div>
                  <h3>{formatMessage(messages.G2_P3_S2_Title)}</h3>

                  <div className={styles.titleWithButton}>
                    <h4>{formatMessage(messages.G2_P3_S2_P1_Title)}</h4>
                    <Button
                      size={"small"}
                      theme={"primary"}
                      shape={"round"}
                      icon={"cil-file-add"}
                      onClick={() => setNewSessionModalOpen(true)}
                    >
                      {formatMessage(messages.G2_P3_S2_P1_ButtonLabel)}
                    </Button>
                  </div>
                  {nl2p(formatMessage(messages.G2_P3_S2_P1_Description))}

                  <div className={styles.titleWithButton}>
                    <h4>{formatMessage(messages.G2_P3_S2_P2_Title)}</h4>
                    <Button
                      size={"small"}
                      theme={"primary"}
                      shape={"round"}
                      icon={"cil-color-border"}
                      onClick={() => setMasterBusModalOpen(true)}
                    >
                      {formatMessage(messages.G2_P3_S2_P2_ButtonLabel)}
                    </Button>
                  </div>
                  {nl2p(formatMessage(messages.G2_P3_S2_P2_Description))}
                </div>
              </div>,
              // Second carousel item - Recording Tracks
              <div key={2} className={styles.oneColumn}>
                <div>
                  <h3>{formatMessage(messages.G2_P3_S3_Title)}</h3>

                  <div className={styles.titleWithButton}>
                    <h4>{formatMessage(messages.G2_P3_S3_P1_Title)}</h4>
                    <Button
                      size={"small"}
                      theme={"primary"}
                      shape={"round"}
                      icon={"cil-input-component"}
                      onClick={() => setRecordingTracksModalOpen(true)}
                    >
                      {formatMessage(messages.G2_P3_S3_P1_ButtonLabel)}
                    </Button>
                  </div>
                  {nl2p(formatMessage(messages.G2_P3_S3_P1_Description))}

                  <div className={styles.titleWithButton}>
                    <h4>{formatMessage(messages.G2_P3_S3_P2_Title)}</h4>
                  </div>
                  {nl2p(formatMessage(messages.G2_P3_S3_P2_Description))}

                  <div className={styles.titleWithButton}>
                    <h4>{formatMessage(messages.G2_P3_S3_P3_Title)}</h4>
                  </div>
                  {nl2p(formatMessage(messages.G2_P3_S3_P3_Description))}
                </div>
                ,
              </div>,
            ]}
          </SpatialCarousel>
        </div>

        {/* Modals */}
        <NewSessionModal visible={newSessionModalOpen} onClose={handleCloseNewSessionModal} />
        <MasterBusModal visible={masterBusModalOpen} onClose={handleCloseMasterBusModal} />
        <RecordingTracksModal visible={recordingTracksModalOpen} onClose={handleCloseRecordingTracksModal} />
      </div>
    </SimpleLayoutView>
  );
};

export const Slide3: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  const blocks = useBlocks();
  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P3_S4_Title)}</h3>
            <div className={styles.twoColumns}>
              <div className={styles.column}>{nl2p(formatMessage(messages.G2_P3_S4_Description))}</div>
              <div className={styles.column}>
                <div className={styles.image}>
                  <blocks.image src={`${appConfig.STATIC_ASSETS_URL}/lms/guide/guide02/mic_arrays.png`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SimpleLayoutView>
  );
};

export const Slide4: React.FC<SlideProps> = () => {
  const { formatMessage } = useIntl();
  return (
    <SimpleLayoutView>
      <div className={styles.root}>
        <div className={styles.rightComponent}>
          <div className={styles.oneColumn}>
            <h3>{formatMessage(messages.G2_P3_S5_Title)}</h3>
            {nl2p(formatMessage(messages.G2_P3_S5_Description))}
          </div>
        </div>
      </div>
    </SimpleLayoutView>
  );
};
